import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Usb, RefreshCw, CheckCircle, AlertCircle, ArrowRight, HelpCircle, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useDevice } from '../contexts/DeviceContext';
import { useAuth } from '../contexts/AuthContext';
import TroubleshootingSection from './TroubleshootingSection';

const DeviceDetection = () => {
  const { devices, selectedDevice, isScanning, scanForDevices, selectDevice } = useDevice();
  const { selectedChild, device } = useAuth();
  const navigate = useNavigate();
  const [autoScanned, setAutoScanned] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  const isWindows = useMemo(() => {
    return navigator.platform.toLowerCase().includes('win') || 
           navigator.userAgent.toLowerCase().includes('windows');
  }, []);

  useEffect(() => {
    if (!autoScanned) {
      scanForDevices();
      setAutoScanned(true);
    }
  }, [autoScanned, scanForDevices]);

  const handleDeviceSelect = (device: any) => {
    selectDevice(device);
  };

  const handleContinue = () => {
    navigate('/find-my-instructions');
  };

  const getStatusBadge = (device: any) => {
    if (device.isConnected) {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Connected</Badge>;
    }
    return <Badge variant="secondary">Disconnected</Badge>;
  };

  const handleTroubleshootingClick = () => {
    setShowTroubleshooting(true);
  };

  const handleBackFromTroubleshooting = () => {
    setShowTroubleshooting(false);
  };

  if (showTroubleshooting) {
    return <TroubleshootingSection onBack={handleBackFromTroubleshooting} />;
  }

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-card rounded-3xl shadow-xl overflow-hidden border border-border flex min-h-[700px]">
        
        {/* Left Side - Info */}
        <div className="flex-1 bg-gradient-to-br from-primary/5 via-background to-accent/5 flex flex-col items-center justify-center p-10 relative">
          <div className="absolute top-0 left-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 max-w-sm text-center space-y-10">
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                Device Setup
              </h2>
              <p className="text-muted-foreground mt-2">
                Connect and configure your device
              </p>
            </div>

            {/* Quick Setup Steps */}
            <div className="bg-card/80 backdrop-blur-sm rounded-2xl p-6 border border-border shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Usb className="w-5 h-5 text-primary" />
                </div>
                <h4 className="text-base font-semibold text-foreground">Quick Setup</h4>
              </div>
              <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground text-left">
                <li>Connect iPhone via USB cable</li>
                <li>Unlock device and trust this computer</li>
                <li>Select your device from the list</li>
                <li>Continue to next step</li>
              </ol>
              
              {isWindows && (
                <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                  <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    iTunes must be installed on your computer for the Windows tool to work.
                  </p>
                </div>
              )}
              
              <div className="mt-5 pt-5 border-t border-border">
                <button
                  onClick={handleTroubleshootingClick}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>Had problems connecting?</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Device Detection */}
        <div className="flex-1 bg-muted/30 flex flex-col p-8">
          <div className="w-full max-w-md mx-auto h-full flex flex-col">
            <div className="bg-card rounded-2xl shadow-sm border border-border p-6 h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-foreground">Available Devices</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scanForDevices}
                  disabled={isScanning}
                  className="rounded-xl h-9"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isScanning ? 'animate-spin' : ''}`} />
                  {isScanning ? 'Scanning' : 'Refresh'}
                </Button>
              </div>

              {/* Device List */}
              <div className="flex-1 overflow-y-auto">
                {devices.length === 0 && !isScanning && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-5">
                      <AlertCircle className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h4 className="text-lg font-semibold text-foreground mb-2">No Devices Found</h4>
                    <p className="text-sm text-muted-foreground mb-6">
                      Make sure your iPhone is connected via USB and you've trusted this computer.
                    </p>
                    <Button onClick={scanForDevices} variant="outline" className="rounded-xl">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Scan Again
                    </Button>
                  </div>
                )}

                {devices.length > 0 && (
                  <div className="space-y-3">
                    {devices.map((deviceItem) => (
                      <Card
                        key={deviceItem.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedDevice?.id === deviceItem.id
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-border hover:border-primary/30'
                        }`}
                        onClick={() => handleDeviceSelect(deviceItem)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                selectedDevice?.id === deviceItem.id ? 'bg-primary' : 'bg-muted'
                              }`}>
                                <Smartphone className={`w-6 h-6 ${
                                  selectedDevice?.id === deviceItem.id ? 'text-primary-foreground' : 'text-muted-foreground'
                                }`} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-foreground">{deviceItem.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {deviceItem.model} • iOS {deviceItem.iosVersion}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(deviceItem)}
                              {selectedDevice?.id === deviceItem.id && (
                                <CheckCircle className="w-5 h-5 text-primary" />
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {isScanning && (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-5" />
                    <h4 className="text-lg font-semibold text-foreground mb-2">Scanning for Devices</h4>
                    <p className="text-sm text-muted-foreground">Looking for connected iPhones...</p>
                  </div>
                )}
              </div>

              {/* Info Alert */}
              {devices.length === 0 && !isScanning && (
                <Alert className="mt-5 bg-primary/5 border-primary/20">
                  <AlertDescription className="text-sm text-foreground">
                    Remember to press "trust this device" on your phone.
                  </AlertDescription>
                </Alert>
              )}

              {/* Continue Button */}
              {selectedDevice && (
                <div className="mt-6 pt-5 border-t border-border">
                  <Button
                    onClick={handleContinue}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl h-12 font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all group"
                  >
                    Continue Setup
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetection;
